import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { getActiveEditable, getFocusedScroller, ListenerGroup } from "./shared/keyboard-viewport.util";
import { RpKeyboardStateService } from "./keyboard-state.service";

// While the keyboard is open, --mydocma-app-height flickers to full height for the instant a
// focus swap passes through <body> (app-container's iOS restore quirk, app-container.component.ts
// refreshViewportHeightVar). The momentarily grown scroller clamps its scrollTop back to the
// pre-keyboard max, yanking the view above the focused field. Capping the focused scroller's
// max-height for the keyboard session turns that flicker into a no-op. Height writes only.
@Injectable({ providedIn: 'root' })
export class RpKeyboardScrollerFreezeService {

    private listeners = new ListenerGroup();
    private stateSub: Subscription | null = null;
    private frozen = new Set<HTMLElement>();

    constructor(private keyboardState: RpKeyboardStateService) {
    }

    start(): void {
        if (this.stateSub || typeof document === 'undefined') {
            return;
        }
        this.listeners.add(document, 'focusin', () => this.freezeActiveScroller(), { capture: true });
        this.stateSub = this.keyboardState.keyboardOpen$.subscribe(open => {
            if (open) {
                requestAnimationFrame(() => this.freezeActiveScroller());
            } else {
                this.restoreAll();
            }
        });
    }

    private freezeActiveScroller(): void {
        if (!this.keyboardState.isKeyboardOpen) {
            return;
        }
        const active = getActiveEditable();
        if (!active) {
            return;
        }
        const scroller = getFocusedScroller(active);
        if (scroller === document.documentElement || scroller === document.scrollingElement || this.frozen.has(scroller)) {
            return;
        }
        scroller.style.setProperty('max-height', `${scroller.clientHeight}px`, 'important');
        this.frozen.add(scroller);
    }

    private restoreAll(): void {
        this.frozen.forEach(el => el.style.removeProperty('max-height'));
        this.frozen.clear();
    }
}
